import React from 'react';

import Suspense from '@atlaskit/react-ufo/suspense';
import { useOrgIdState } from '@atlassian/standalone-directory/org-id';
import { usePrincipalUser } from '@atlassian/standalone-directory/ptc-common';
import { useSiteStandaloneApp } from '@atlassian/standalone-directory/site-standalone-app';
import useOrgIdQuery from '@atlassian/standalone-directory/use-org-id-query';

import { useCurrentUser } from '@townsquare/user-store';

export type BootstrapResult = {
	orgId?: string;
	cloudId?: string;
	accountId?: string;
};

type BootstrapProps = {
	children: (data: BootstrapResult) => JSX.Element;
};

export function Bootstrap({ children }: BootstrapProps): React.JSX.Element | null {
	const [{ cloudId }, { getCloudId }] = useSiteStandaloneApp();
	const [, { setOrgId }] = useOrgIdState();
	const { orgId, isLoading: isOrgIdLoading } = useOrgIdQuery({
		component: 'teams-isolated',
	});
	const [{ principalUserId }, { setPrincipalUserInfo }] = usePrincipalUser();
	const currentUser = useCurrentUser();
	const [isCloudIdLoading, setIsCloudIdLoading] = React.useState(true);

	const hasCloudId = !!cloudId && cloudId !== 'undefined';

	// Step 1: Fetch cloudId first - this is the foundation for everything else
	React.useEffect(() => {
		if (hasCloudId) {
			setIsCloudIdLoading(false);
		} else {
			setIsCloudIdLoading(true);
			void getCloudId();
		}
	}, [hasCloudId, getCloudId]);

	// Step 2: After cloudId is available, initialize OrgId
	React.useEffect(() => {
		if (hasCloudId && orgId && !isOrgIdLoading) {
			setOrgId(orgId);
		}
	}, [hasCloudId, isOrgIdLoading, orgId, setOrgId]);

	// Step 3: Populate principal user from store
	React.useEffect(() => {
		if (!currentUser.accountId) {
			return;
		}

		const principalUserInfoMap = {
			principalUserId: currentUser.accountId,
			userId: currentUser.accountId,
			email: currentUser.email,
			fullName: currentUser.name,
			avatarUrl: currentUser.picture,
			title: currentUser.jobTitle,
			locale: currentUser.locale,
		};
		setPrincipalUserInfo(principalUserInfoMap);
	}, [currentUser, setPrincipalUserInfo]);

	if (isCloudIdLoading || !hasCloudId || isOrgIdLoading || !currentUser.accountId) {
		return null;
	}

	const bootstrapData = {
		orgId,
		cloudId,
		accountId: principalUserId,
	};

	return (
		<Suspense interactionName="spa-loader" fallback={null}>
			{children(bootstrapData)}
		</Suspense>
	);
}
